import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
} from '@reduxjs/toolkit';
import { rejectValue, flatTransfer } from 'redux/helpers';
import format from 'date-fns/format';

import { GET_TRANSFERS, GET_TRANSFERS_BY_ID, GET_WEBHOOKS } from 'services/api';
import {
  TRANSFER,
  TRANSFER_ALL,
  TRANSFER_SEARCH,
  TRANSFER_BY_ID,
  TRANSFER_WEBHOOKS,
} from './types';
import { last90Days } from 'components/DateRangePicker/ranges';
import { DATE_FOR_API } from 'utils/dateFormat';

const transferAdapter = createEntityAdapter();

export const initialState = transferAdapter.getInitialState({
  isLoading: false,
  detailsIsLoading: false,
  pagination: {
    total: 1,
    total_pages: 1,
    current_page: 1,
    per_page: 25,
    count: 1,
  },
  params: {
    per_page: 25,
    from: format(last90Days.startDate, DATE_FOR_API),
    to: format(last90Days.endDate, DATE_FOR_API),
  },
  search: {
    term: '',
    submitted: false,
  },
});

export const getTransfers = createAsyncThunk(
  TRANSFER_ALL,
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      const companyId = getState().company.current;
      const { data, meta } = await GET_TRANSFERS({
        ...getState().transfer.params,
        company_id: companyId,
      });

      if (!data.length) return { data: [], pagination: meta.pagination };
      const _data = data.map(flatTransfer);

      return { data: _data, pagination: meta.pagination };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const searchTransfers = createAsyncThunk(
  TRANSFER_SEARCH,
  async (_, { rejectWithValue, getState }) => {
    try {
      const companyId = getState().company.current;
      const { term } = getState().transfer.search;

      if (!term) {
        return;
      }

      const { data, meta } = await GET_TRANSFERS({
        term,
        company_id: companyId,
      });

      if (!data.length) return { data: [], pagination: meta.pagination };

      const _data = data.map(flatTransfer);

      return { data: _data, pagination: meta.pagination };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const getTransfersById = createAsyncThunk(
  TRANSFER_BY_ID,
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await GET_TRANSFERS_BY_ID(id);

      return flatTransfer(data);
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const getTransferWebhooks = createAsyncThunk(
  TRANSFER_WEBHOOKS,
  async (
    { transfer_id, page = 1 },
    { rejectWithValue, getState, dispatch }
  ) => {
    try {
      const companyId = getState().company.current;

      const { data, meta } = await GET_WEBHOOKS({
        transfer_id,
        page,
        per_page: 10,
        company_id: companyId,
      });

      if (!data.length) return { data: [], pagination: meta.pagination };

      return { data, pagination: meta.pagination };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

const transferSlice = createSlice({
  name: TRANSFER,
  initialState,
  reducers: {
    setParams: (state, { payload }) => {
      state.params = { ...state.params, ...payload };
    },
    setSearchTerm: (state, { payload }) => {
      state.search.term = payload;
    },
    resetSearch: (state) => {
      state.search = initialState.search;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTransfers.pending, (state) => {
        state.isLoading = true;
        state.search.submitted = false;
      })
      .addCase(getTransfers.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getTransfers.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        transferAdapter.setAll(state, payload.data);
        state.pagination = payload.pagination;
      })
      .addCase(searchTransfers.pending, (state) => {
        state.isLoading = true;
        state.search.submitted = true;
      })
      .addCase(searchTransfers.rejected, (state) => {
        state.isLoading = false;
        state.search.submitted = false;
      })
      .addCase(searchTransfers.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        transferAdapter.setAll(state, payload.data);
        state.pagination = payload.pagination;
      })
      .addCase(getTransfersById.pending, (state) => {
        state.detailsIsLoading = true;
      })
      .addCase(getTransfersById.rejected, (state) => {
        state.detailsIsLoading = false;
      })
      .addCase(getTransfersById.fulfilled, (state, { payload }) => {
        state.detailsIsLoading = false;
        transferAdapter.upsertOne(state, payload);
      })
      .addCase(getTransferWebhooks.fulfilled, (state, action) => {
        transferAdapter.updateOne(state, {
          id: action.meta.arg.transfer_id,
          changes: {
            webhooks: {
              data: action.payload.data,
              pagination: {
                ...action.payload.pagination,
                hasStaticPerPage: true,
              },
            },
          },
        });
      })
      .addDefaultCase((state) => state);
  },
});

export const transferSelectors = transferAdapter.getSelectors(
  (state) => state.transfer
);

export const transferByIdSelector = (state, id) => ({
  data: transferSelectors.selectById(state, id),
  isLoading: state.transfer.detailsIsLoading,
});

export const transferAllSelector = (state) => ({
  data: transferSelectors.selectAll(state),
  pagination: state.transfer.pagination,
  isLoading: state.transfer.isLoading,
  params: state.transfer.params,
  searchTerm: state.transfer.search.term,
});
export const { setParams, setSearchTerm, resetSearch } = transferSlice.actions;

export default transferSlice.reducer;
